import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtAmountTr, CUR_LABEL, trFold } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Search, TrendingUp, TrendingDown, Banknote, Percent, ArrowUpDown, ArrowUp, ArrowDown, FileSpreadsheet, Factory } from "lucide-react";

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, ReferenceLine } from "recharts";

const zoneCls = {
  "Blue Zone": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Green Zone": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};

export const ZoneFilterButtons = ({ zones, value, onChange, testidPrefix }) => (
  <div className="flex items-center gap-2">
    {["all", ...zones].map((z) => (
      <button
        key={z}
        type="button"
        data-testid={`${testidPrefix}-zone-${z === "all" ? "all" : z.toLowerCase().replace(/\s+/g, "-")}`}
        onClick={() => onChange(z)}
        className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border transition-colors duration-150 ${
          value === z
            ? z === "Blue Zone"
              ? "bg-blue-600 border-blue-600 text-white"
              : z === "Green Zone"
                ? "bg-emerald-600 border-emerald-600 text-white"
                : "bg-slate-900 border-slate-900 text-white"
            : "bg-white border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900"
        }`}
      >
        {z === "all" ? "Tümü" : z}
      </button>
    ))}
  </div>
);

export default function ProfitAnalysisPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [zoneSel, setZoneSel] = useState("all");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const myZones = user?.zones || [];
  const canProdPrice = ["username", "contact_email", "email"].some((k) => (user?.[k] || "").toLowerCase() === "muratbucak@quattrostand.com");

  useEffect(() => {
    if (!canProdPrice) { navigate("/atayici"); return; }
    api.get("/assigner/profit-analysis")
      .then((r) => { setRows(r.data.projects); setRates(r.data.rates); setLoaded(true); })
      .catch((e) => { toast.error(apiError(e)); setLoaded(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toTL = (amt, cur) => {
    if (amt == null) return null;
    const c = cur || "TRY";
    if (c === "TRY") return amt;
    const r = rates?.[c === "USD" ? "USD" : "EUR"];
    return r ? amt * r : null;
  };

  const enriched = useMemo(() => rows.map((p) => {
    const offerTL = Number(p.offer_amount) > 0 ? toTL(Number(p.offer_amount), p.offer_currency) : null;
    const costTL = Number(p.producer_price) > 0 ? toTL(Number(p.producer_price), p.producer_price_currency) : null;
    const diff = offerTL != null && costTL != null ? offerTL - costTL : null;
    const margin = diff != null && offerTL > 0 ? (diff / offerTL) * 100 : null;
    return { ...p, offerTL, costTL, diff, margin };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [rows, rates]);

  const filtered = useMemo(() => {
    const f = trFold(q.trim());
    let list = zoneSel === "all" ? enriched : enriched.filter((p) => p.zone === zoneSel);
    if (f) list = list.filter((p) => trFold(`${p.name} ${p.producer_name} ${p.stand_no || ""}`).includes(f));
    const dir = sort.dir === "asc" ? 1 : -1;
    const val = (p) => {
      switch (sort.key) {
        case "offer": return p.offerTL ?? -Infinity;
        case "cost": return p.costTL ?? -Infinity;
        case "diff": return p.diff ?? -Infinity;
        case "margin": return p.margin ?? -Infinity;
        default: return null;
      }
    };
    return [...list].sort((a, b) => {
      if (sort.key === "name") return dir * (a.name || "").localeCompare(b.name || "", "tr");
      if (sort.key === "producer") return dir * (a.producer_name || "").localeCompare(b.producer_name || "", "tr");
      return dir * (val(a) - val(b));
    });
  }, [enriched, q, sort, zoneSel]);

  const byProducer = useMemo(() => {
    const m = new Map();
    filtered.filter((p) => p.diff != null).forEach((p) => {
      const key = p.producer_name;
      if (!m.has(key)) m.set(key, { name: key, count: 0, offer: 0, cost: 0 });
      const g = m.get(key);
      g.count += 1;
      g.offer += p.offerTL;
      g.cost += p.costTL;
    });
    return [...m.values()]
      .map((g) => ({ ...g, diff: g.offer - g.cost, margin: g.offer > 0 ? ((g.offer - g.cost) / g.offer) * 100 : null }))
      .sort((a, b) => b.diff - a.diff);
  }, [filtered]);

  const byProject = useMemo(() => filtered
    .filter((p) => p.diff != null)
    .map((p) => ({ name: p.stand_no ? `${p.name} (${p.stand_no})` : p.name, diff: p.diff, margin: p.margin }))
    .sort((a, b) => b.diff - a.diff), [filtered]);

  const totals = useMemo(() => {
    const both = filtered.filter((p) => p.diff != null);
    const offer = both.reduce((s, p) => s + p.offerTL, 0);
    const cost = both.reduce((s, p) => s + p.costTL, 0);
    const diff = offer - cost;
    return { count: both.length, offer, cost, diff, margin: offer > 0 ? (diff / offer) * 100 : null };
  }, [filtered]);

  const fmtTL = (n) => (n == null ? "—" : `₺${fmtAmountTr(n)}`);
  const fmtPct = (n) => (n == null ? "—" : `%${fmtAmountTr(n)}`);
  const diffCls = (n) => (n == null ? "text-slate-400" : n >= 0 ? "text-emerald-600" : "text-red-600");

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const data = filtered.map((p, i) => ({
      "#": i + 1,
      "Proje": p.name || "",
      "Bölge": p.zone || "",
      "Stand No": p.stand_no || "",
      "Üretici": p.producer_name || "",
      "Teklif Tutarı": Number(p.offer_amount) > 0 ? `${fmtAmountTr(p.offer_amount)} ${CUR_LABEL[p.offer_currency || "TRY"]}` : "",
      "Teklif (₺)": p.offerTL != null ? fmtAmountTr(p.offerTL) : "",
      "Üretici Fiyatı": Number(p.producer_price) > 0 ? `${fmtAmountTr(p.producer_price)} ${CUR_LABEL[p.producer_price_currency || "TRY"]}` : "",
      "Maliyet (₺)": p.costTL != null ? fmtAmountTr(p.costTL) : "",
      "Fark (₺)": p.diff != null ? fmtAmountTr(p.diff) : "",
      "Kar Oranı": p.margin != null ? `%${fmtAmountTr(p.margin)}` : "",
    }));
    data.push({
      "#": "", "Proje": `Genel Toplam (${totals.count} proje — her iki tutar girilmiş)`, "Bölge": "", "Stand No": "", "Üretici": "",
      "Teklif Tutarı": "", "Teklif (₺)": fmtAmountTr(totals.offer), "Üretici Fiyatı": "", "Maliyet (₺)": fmtAmountTr(totals.cost),
      "Fark (₺)": fmtAmountTr(totals.diff), "Kar Oranı": totals.margin != null ? `%${fmtAmountTr(totals.margin)}` : "",
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [{ wch: 4 }, { wch: 40 }, { wch: 12 }, { wch: 10 }, { wch: 24 }, { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 12 }];
    const sData = byProducer.map((g) => ({
      "Üretici": g.name, "Proje": g.count, "Teklif (₺)": fmtAmountTr(g.offer), "Maliyet (₺)": fmtAmountTr(g.cost),
      "Fark (₺)": fmtAmountTr(g.diff), "Kar Oranı": g.margin != null ? `%${fmtAmountTr(g.margin)}` : "",
    }));
    const ws2 = XLSX.utils.json_to_sheet(sData);
    ws2["!cols"] = [{ wch: 28 }, { wch: 8 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Kar-Zarar");
    XLSX.utils.book_append_sheet(wb, ws2, "Üretici Özeti");
    XLSX.writeFile(wb, `kar-zarar-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`${filtered.length} proje Excel'e aktarıldı`);
  };

  const SortTh = ({ k, label, align = "right" }) => (
    <th className={`text-${align} px-4 py-3 font-semibold`}>
      <button
        type="button"
        onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))}
        data-testid={`pa-sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 transition-colors duration-150 ${sort.key === k ? "text-slate-700" : ""}`}
      >
        {label}
        {sort.key === k ? (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="text-slate-300" />}
      </button>
    </th>
  );

  const Kpi = ({ icon: Icon, label, value, sub, accent, testid }) => (
    <div className="bg-white border border-slate-200 p-5" data-testid={testid}>
      <div className={`w-10 h-10 flex items-center justify-center ${accent}`}><Icon size={18} /></div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mt-4">{label}</div>
      <div className="font-heading text-2xl font-extrabold tracking-tight tabular-nums mt-1 break-words">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="profit-analysis-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><TrendingUp size={10} /> Kar/Zarar Analizi</div>
          </div>
          <button
            onClick={() => navigate("/atayici")}
            data-testid="profit-analysis-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 px-3 py-2 transition-colors duration-150"
          >
            <ArrowLeft size={14} /> Geri Dön
          </button>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Kar/Zarar Analizi</h1>
            <p className="text-sm text-slate-500 mt-1">
              {loaded ? `${filtered.length} proje` : "Yükleniyor..."} — fark ve kar oranı TL bazında hesaplanır
              {rates?.USD && rates?.EUR && <> · TCMB {rates.date}: $ {fmtAmountTr(rates.USD)} · € {fmtAmountTr(rates.EUR)}</>}
            </p>
            <div className="mt-4">
              <ZoneFilterButtons zones={myZones} value={zoneSel} onChange={setZoneSel} testidPrefix="pa" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proje, üretici ara..." data-testid="profit-analysis-search" className="rounded-none border-slate-200 h-10 pl-9 w-64 bg-white" />
            </div>
            <Button variant="outline" onClick={exportExcel} data-testid="profit-analysis-export-excel" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
              <FileSpreadsheet size={15} /> Excel
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pa-kpi-cards">
          <Kpi icon={Banknote} label="Toplam Teklif" value={fmtTL(totals.offer)} sub={`${totals.count} proje (her iki tutar girilmiş)`} accent="bg-blue-500/10 text-blue-600" testid="pa-kpi-offer" />
          <Kpi icon={Banknote} label="Toplam Üretici Maliyeti" value={fmtTL(totals.cost)} sub={`${totals.count} proje`} accent="bg-amber-500/10 text-amber-600" testid="pa-kpi-cost" />
          <Kpi icon={totals.diff >= 0 ? TrendingUp : TrendingDown} label="Toplam Fark" value={<span className={diffCls(totals.diff)}>{fmtTL(totals.diff)}</span>} accent={totals.diff >= 0 ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600"} testid="pa-kpi-diff" />
          <Kpi icon={Percent} label="Ortalama Kar Oranı" value={<span className={diffCls(totals.margin)}>{fmtPct(totals.margin)}</span>} accent="bg-slate-900/5 text-slate-700" testid="pa-kpi-margin" />
        </div>

        {byProject.length > 0 && (
          <section className="space-y-3" data-testid="pa-profit-chart">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5"><TrendingUp size={13} /> Proje Bazında Kar (₺)</h2>
            <div className="bg-white border border-slate-200 p-5">
              <div style={{ height: Math.max(220, byProject.length * 32 + 40) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byProject} layout="vertical" margin={{ top: 4, right: 32, left: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `₺${new Intl.NumberFormat("tr-TR", { notation: "compact", maximumFractionDigits: 1 }).format(v)}`} />
                    <YAxis type="category" dataKey="name" width={220} interval={0} tick={{ fontSize: 10, fill: "#334155" }} />
                    <Tooltip
                      formatter={(v, n, { payload }) => [`₺${fmtAmountTr(v)}${payload.margin != null ? ` · Kar oranı %${fmtAmountTr(payload.margin)}` : ""}`, "Kar"]}
                      labelFormatter={(l) => l}
                      contentStyle={{ borderRadius: 0, border: "1px solid #e2e8f0", fontSize: 12 }}
                    />
                    <ReferenceLine x={0} stroke="#94a3b8" />
                    <Bar dataKey="diff" barSize={18}>
                      {byProject.map((g) => (
                        <Cell key={g.name} fill={g.diff >= 0 ? "#059669" : "#dc2626"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>
        )}

        {byProducer.length > 0 && (
          <section className="space-y-3" data-testid="pa-producer-summary">
            <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5"><Factory size={13} /> Üretici Bazında Kar Özeti</h2>
            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-400">
                    <th className="text-left px-4 py-3 font-semibold">Üretici</th>
                    <th className="text-right px-4 py-3 font-semibold">Proje</th>
                    <th className="text-right px-4 py-3 font-semibold">Teklif (₺)</th>
                    <th className="text-right px-4 py-3 font-semibold">Maliyet (₺)</th>
                    <th className="text-right px-4 py-3 font-semibold">Toplam Kar (₺)</th>
                    <th className="text-right px-4 py-3 font-semibold">Kar Oranı</th>
                  </tr>
                </thead>
                <tbody>
                  {byProducer.map((g) => (
                    <tr key={g.name} className="border-b border-slate-100 last:border-0" data-testid={`pa-producer-row-${g.name}`}>
                      <td className="px-4 py-3 font-semibold text-slate-800">{g.name}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-500">{g.count}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtTL(g.offer)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtTL(g.cost)}</td>
                      <td className={`px-4 py-3 text-right tabular-nums font-semibold ${diffCls(g.diff)}`}>{fmtTL(g.diff)}</td>
                      <td className={`px-4 py-3 text-right tabular-nums font-semibold ${diffCls(g.margin)}`}>{fmtPct(g.margin)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-900 bg-slate-50 font-semibold" data-testid="pa-producer-total-row">
                    <td className="px-4 py-3">Genel Toplam</td>
                    <td className="px-4 py-3 text-right tabular-nums">{totals.count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtTL(totals.offer)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtTL(totals.cost)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${diffCls(totals.diff)}`}>{fmtTL(totals.diff)}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${diffCls(totals.margin)}`}>{fmtPct(totals.margin)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        )}

        <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="profit-analysis-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-400">
                <SortTh k="name" label="Proje" align="left" />
                <SortTh k="producer" label="Üretici" align="left" />
                <SortTh k="offer" label="Teklif Tutarı" />
                <SortTh k="cost" label="Üretici Fiyatı" />
                <SortTh k="diff" label="Fark (₺)" />
                <SortTh k="margin" label="Kar Oranı" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors duration-100" data-testid={`pa-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{p.name}</div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-slate-400">
                      {p.zone && <span className={`px-1.5 py-0.5 border ${zoneCls[p.zone] || "border-slate-200"}`}>{p.zone}</span>}
                      {p.stand_no && <span>Stand: {p.stand_no}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{p.producer_name}</td>
                  <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                    {Number(p.offer_amount) > 0 ? <>
                      <div className="font-semibold">{fmtAmountTr(p.offer_amount)} {CUR_LABEL[p.offer_currency || "TRY"]}</div>
                      {(p.offer_currency || "TRY") !== "TRY" && <div className="text-[10px] text-slate-400">{fmtTL(p.offerTL)}</div>}
                    </> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                    {Number(p.producer_price) > 0 ? <>
                      <div className="font-semibold">{fmtAmountTr(p.producer_price)} {CUR_LABEL[p.producer_price_currency || "TRY"]}</div>
                      {(p.producer_price_currency || "TRY") !== "TRY" && <div className="text-[10px] text-slate-400">{fmtTL(p.costTL)}</div>}
                    </> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums font-semibold whitespace-nowrap ${diffCls(p.diff)}`}>{fmtTL(p.diff)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums font-semibold whitespace-nowrap ${diffCls(p.margin)}`}>{fmtPct(p.margin)}</td>
                </tr>
              ))}
              {loaded && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Teklif tutarı veya üretici fiyatı girilmiş proje bulunamadı.</td></tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-slate-900 bg-slate-50 font-semibold" data-testid="pa-total-row">
                  <td className="px-4 py-3" colSpan={2}>Genel Toplam ({totals.count} proje — her iki tutar girilmiş)</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtTL(totals.offer)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{fmtTL(totals.cost)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums ${diffCls(totals.diff)}`}>{fmtTL(totals.diff)}</td>
                  <td className={`px-4 py-3 text-right tabular-nums ${diffCls(totals.margin)}`}>{fmtPct(totals.margin)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </main>
    </div>
  );
}
